// Écran « Locations » : ce que le module rapporte ce mois-ci, les camions loués
// en ce moment, et ceux à reprendre.
//
// CE QUE L'ÉCRAN DOIT DIRE EN PREMIER, c'est le cas qui coûte de l'argent : un
// camion dont la fin de location est DÉPASSÉE sans qu'il soit rendu. La liste
// arrive donc triée par date de fin croissante (le serveur), et la situation —
// en cours / se termine bientôt / en retard de restitution — est CALCULÉE PAR
// LE SERVEUR (`location_calculs.situation`) : le navigateur d'un client peut
// être à une autre date que la nôtre, et le seuil des 30 jours est le même que
// celui de l'alerte e-mail.
//
// LE TRAVAILLEUR LIT, L'ADMINISTRATEUR OUVRE. Les loyers sont visibles du
// travailleur (décision de Yanis, 12/10). Le bouton « Nouvelle location » n'est
// donc montré qu'à l'administrateur : le serveur refuserait les autres de toute
// façon, et proposer une porte fermée passe pour un bug.
//
// UNE LOCATION SE DÉMARRE ICI, SANS PASSER PAR LA FICHE DU CAMION (décision de
// Yanis, 20/10) : on tape la plaque, on choisit, et l'on arrive dans le même
// assistant que depuis la fiche. Même principe que l'écran Financement.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import api, { chargerTousLesVehicules, messageErreur } from "../api";
import ComboBox from "../components/ComboBox";
import Layout from "../components/Layout";
import ModaleConfirmation from "../components/ModaleConfirmation";
import { SqueletteTableau } from "../components/Squelette";
import { ecartMoisJours, formaterDate, formaterEuros } from "../format";

// Une carte d'indicateur, de la même forme que celles du tableau de bord.
function Carte({ label, valeur, accent, retard }) {
  return (
    <div
      className={
        "carte-indicateur" + (accent ? " carte-accent" : "") + (retard ? " carte-retard" : "")
      }
    >
      <span className="indicateur-valeur">{valeur}</span>
      <span className="indicateur-label">{label}</span>
    </div>
  );
}

const ONGLETS = [
  { valeur: "en_cours", label: "En cours" },
  { valeur: "terminees", label: "Terminées" },
];

/** La ligne « où en est cette location », avec son niveau d'urgence. */
function situation(l) {
  if (l.statut === "brouillon") {
    return { texte: "En préparation", detail: "contrat pas encore signé", urgent: false };
  }
  if (!l.actif) {
    return {
      texte: "Terminée",
      detail: l.date_restitution ? `rendue le ${formaterDate(l.date_restitution)}` : "",
      urgent: false,
    };
  }
  const ecart = ecartMoisJours(l.date_fin);
  const duree = ecart ? ecart.texte : `${Math.abs(l.jours_avant_fin)} j`;
  if (l.situation === "en_retard") {
    return {
      texte: "En retard de restitution",
      detail: `fin dépassée depuis ${duree}`,
      urgent: true,
    };
  }
  if (l.situation === "bientot") {
    return { texte: "Se termine bientôt", detail: `dans ${duree}`, urgent: true };
  }
  return { texte: "En cours", detail: `dans ${duree}`, urgent: false };
}

export default function MesLocations({ user, onLogout }) {
  const navigate = useNavigate();
  const [onglet, setOnglet] = useState("en_cours");
  const [lignes, setLignes] = useState(null);
  const [erreur, setErreur] = useState("");
  // Ce que les locations rapportent CE MOIS. Elle vit ICI et non au tableau de
  // bord : c'est le module qui la porte (décision de Yanis, 19/10).
  const [mois, setMois] = useState(null);

  // LA FENÊTRE « NOUVELLE LOCATION ». Les camions ne sont chargés qu'à
  // l'ouverture : la plupart des visites n'en ont pas besoin, et une flotte de
  // plusieurs centaines de camions se parcourt page par page.
  const [ouverte, setOuverte] = useState(false);
  const [disponibles, setDisponibles] = useState(null); // null = en chargement
  const [choix, setChoix] = useState("");
  const [erreurChoix, setErreurChoix] = useState("");

  const ouvrir = () => {
    setChoix("");
    setErreurChoix("");
    setDisponibles(null);
    setOuverte(true);
    // LES CAMIONS DÉJÀ LOUÉS SONT MASQUÉS (décision de Yanis) : un camion ne se
    // loue qu'à un locataire à la fois. Un BROUILLON compte comme loué : il
    // réserve le camion, c'est ce que le serveur refuserait sinon (409). Les
    // véhicules sortis du parc sont déjà écartés par `/vehicules`.
    Promise.all([
      chargerTousLesVehicules(),
      api.get("/locations", { params: { etat: "en_cours" } }),
    ])
      .then(([vehicules, { data: prises }]) => {
        const pris = new Set(prises.map((l) => l.vehicule_id));
        setDisponibles(vehicules.filter((v) => !pris.has(v.id)));
      })
      .catch(() => {
        setDisponibles([]);
        setErreurChoix("Impossible de charger les véhicules.");
      });
  };

  // Une plaque se choisit EXACTEMENT : une saisie à demi tapée n'ouvre rien.
  const camionChoisi = (disponibles || []).find(
    (v) => v.immatriculation.toLowerCase() === choix.trim().toLowerCase()
  );

  useEffect(() => {
    api
      .get("/locations/synthese")
      .then(({ data }) => setMois(data))
      // Muette : une carte en moins vaut mieux qu'un bandeau d'erreur.
      .catch(() => setMois(null));
  }, []);

  useEffect(() => {
    let vivant = true;
    setLignes(null);
    setErreur("");
    api
      .get("/locations", { params: { etat: onglet } })
      .then(({ data }) => vivant && setLignes(data))
      .catch(
        (err) =>
          vivant && setErreur(messageErreur(err, "Impossible de charger les locations."))
      );
    return () => {
      vivant = false;
    };
  }, [onglet]);

  const chargement = lignes === null && !erreur;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="entete-page">
        <h1>Locations</h1>
        {user.role === "admin" && (
          <div className="actions-groupe">
            <button className="btn-accent" onClick={ouvrir}>
              + Nouvelle location
            </button>
          </div>
        )}
      </div>

      {ouverte && (
        <ModaleConfirmation
          titre="Nouvelle location"
          sousTitre="Quel camion voulez-vous louer ?"
          libelleConfirmer="Continuer"
          confirmerDesactive={!camionChoisi}
          onFermer={() => setOuverte(false)}
          onConfirmer={() => navigate(`/vehicules/${camionChoisi.id}/location/nouvelle`)}
          enfants={
            <div className="champ">
              <span className="champ-label">Véhicule</span>
              {disponibles === null ? (
                <p className="note-discrete">Chargement des véhicules…</p>
              ) : (
                <>
                  <ComboBox
                    value={choix}
                    onChange={setChoix}
                    options={disponibles.map((v) => v.immatriculation)}
                    placeholder="Chercher une immatriculation…"
                  />
                  <p className="note-discrete">
                    {disponibles.length === 0
                      ? "Aucun véhicule disponible : tous sont déjà loués ou sortis du parc."
                      : "Seuls les véhicules non loués sont proposés."}
                  </p>
                </>
              )}
              {erreurChoix && <p className="modal-erreur">{erreurChoix}</p>}
            </div>
          }
        />
      )}

      {mois && mois.nb_locations > 0 && (
        <section data-testid="carte-locations">
          <h3 className="titre-bloc">
            Ce mois-ci ·{" "}
            {new Date(`${mois.mois}-01T12:00:00`).toLocaleDateString("fr-FR", {
              month: "long",
              year: "numeric",
            })}
          </h3>
          {/* Trois chiffres, pas un seul : un bénéfice seul serait faux dès
              qu'on se demande ce qu'il compte. Le carburant n'y est pas : le
              locataire le paie. */}
          <div className="grille-indicateurs grille-couts">
            <Carte label="Loyers du mois" valeur={formaterEuros(mois.loyers)} accent />
            <Carte
              label="Coûts imputés (entretien + financement)"
              valeur={formaterEuros(Number(mois.entretien) + Number(mois.financement))}
            />
            <Carte
              label="Bénéfice net"
              valeur={formaterEuros(mois.benefice)}
              retard={Number(mois.benefice) < 0}
            />
          </div>
          <p className="sous-info">
            {mois.nb_locations} location{mois.nb_locations > 1 ? "s" : ""} · estimation au
            prorata des jours loués · entretien {formaterEuros(mois.entretien)} + financement{" "}
            {formaterEuros(mois.financement)} · carburant à la charge du locataire.
            {mois.financements_non_chiffres > 0 &&
              ` ${mois.financements_non_chiffres} financement${
                mois.financements_non_chiffres > 1 ? "s" : ""
              } sans loyer renseigné : le bénéfice est surestimé.`}
          </p>
        </section>
      )}

      <div className="filtres" role="tablist">
        {ONGLETS.map((o) => (
          <button
            key={o.valeur}
            role="tab"
            aria-selected={onglet === o.valeur}
            className={`chip ${onglet === o.valeur ? "chip-actif" : ""}`}
            onClick={() => setOnglet(o.valeur)}
          >
            {o.label}
          </button>
        ))}
      </div>

      {erreur && <p className="erreur">{erreur}</p>}

      {lignes && lignes.length === 0 ? (
        <p className="vide">
          {onglet !== "en_cours"
            ? "Aucune location terminée."
            : user.role === "admin"
            ? "Aucune location en cours. Utilisez « Nouvelle location » pour en démarrer une."
            : "Aucune location en cours."}
        </p>
      ) : (
        !erreur && (
          <table className="tableau">
            <thead>
              <tr>
                <th>Véhicule</th>
                <th>Locataire</th>
                <th>Période</th>
                <th>Loyer mensuel HT</th>
                <th>Situation</th>
              </tr>
            </thead>
            {chargement && <SqueletteTableau lignes={5} colonnes={5} />}
            {lignes && (
              <tbody>
                {lignes.map((l) => {
                  const s = situation(l);
                  return (
                    <tr
                      key={l.id}
                      className={`ligne-cliquable ${s.urgent ? "urgence-rappel" : ""}`}
                      onClick={() => navigate(`/locations/${l.id}`)}
                    >
                      <td>
                        <Link
                          to={`/vehicules/${l.vehicule_id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <strong>{l.immatriculation || "—"}</strong>
                        </Link>
                      </td>
                      <td>{l.locataire || "—"}</td>
                      <td>
                        {formaterDate(l.date_debut)} → {formaterDate(l.date_fin)}
                      </td>
                      <td>{formaterEuros(l.loyer_mensuel)}</td>
                      <td>
                        <span className={s.urgent ? "urgence-retard" : ""}>
                          <strong>{s.texte}</strong>
                        </span>
                        {s.detail && (
                          <>
                            <br />
                            <span className="note-discrete">{s.detail}</span>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        )
      )}
    </Layout>
  );
}
